import { List, Stack } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { AppDrawer, EmptyResult, InfiniteScroll, PrimaryTab, SearchBar } from "components";
import { AppConstant, KeyConstant, LangConstant } from "const";
import { useEffect, useMemo, useRef, useState } from "react";
import { getCommonLang, getNSLang } from "utils/lang.utils";
import { FILE_FILTER_OPTIONS, FILTER_FILE_TYPE, GALLERY_TAB_ARRAY, GALLERY_TABS } from "./gallery.helper";
import GroupMemberFilter from "./components/GroupMemberFilter";
import { getInteractor } from "services/local.service";
import { StorageUtil } from "utils";
import { groupDataByDate } from "utils/date.utils";
import { ListFileByDate } from "pages/ManageFilePage/components/FileList";
import { useConversationContext } from "../MessengerChat/ConversationContext";
import {
  DEFAULT_FILTER,
  FileFilter,
  OTHER_FILE_TYPES,
  PAGING_SIZE,
  TimeFilter,
} from "pages/ManageFilePage/components/FileFilter";
import { FileListLoading } from "pages/ManageFilePage/components";
import { useDispatch } from "react-redux";
import { ConversationActions } from "redux-store";
import { refactorFiles } from "pages/ManageFilePage/file.helper";
import { useAlertContext } from "components/context/AlertContext";

const Gallery = ({ open, groupId, onClose, onCloseAll }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const prefixKey = StorageUtil.getCurrentPrefixKey();
  const isFetchingRef = useRef(false);
  const { groupDetail, clickMedia } = useConversationContext();
  const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID, prefixKey);
  const { showAlert } = useAlertContext();

  const [selectedTab, setSelectedTab] = useState(GALLERY_TABS.all.index);
  const [viewMode, setViewMode] = useState(AppConstant.VIEW_MODE.fetching);
  const [filterCondition, setFilterCondition] = useState({ groupId, isAll: true, ...DEFAULT_FILTER });
  const [isLoadMore, setIsLoadMore] = useState(true);
  const [fileList, setFileList] = useState({}); // List data by date
  const [displayItemTotal, setDisplayItemTotal] = useState(0);

  const otherGroupMembers = useMemo(() => {
    const groupMembers = groupDetail.groupMembers || [];
    const otherMembers = groupMembers.filter(item => item.id !== accountId);
    return otherMembers;
  }, [groupDetail]);

  const handleChangeFilter = condition => setFilterCondition(preState => ({ ...preState, ...condition }));

  const handleFilterByTime = (startDate, endDate) => handleChangeFilter({ startDate, endDate });

  const handleChangeTab = newIndex => {
    setSelectedTab(newIndex);
    switch (newIndex) {
      case GALLERY_TABS.all.index:
        handleChangeFilter({ isAll: true, createdByMe: false, createdByAccountIds: null, offset: 0 });
        break;

      case GALLERY_TABS.sent.index:
        handleChangeFilter({ isAll: false, createdByMe: true, createdByAccountIds: null, offset: 0 });
        break;

      case GALLERY_TABS.shareByOther.index:
        handleChangeFilter({ isAll: false, createdByMe: false, createdByAccountIds: null, offset: 0 });
        break;

      default:
        break;
    }
  };

  const handleSearch = keyword => handleChangeFilter({ keyword, offset: 0 });

  const handleFilterByType = fileTypes => {
    switch (true) {
      // View all file types
      case [Object.values(FILTER_FILE_TYPE).length, 0].includes(fileTypes.length):
        handleChangeFilter({ fileType: null, offset: 0 });
        break;

      // Select other file
      case fileTypes.includes(FILTER_FILE_TYPE.otherFile):
        handleChangeFilter({ fileType: fileTypes.concat(OTHER_FILE_TYPES), offset: 0 });
        break;

      default:
        handleChangeFilter({ fileType: fileTypes, offset: 0 });
        break;
    }
  };

  const handleChangeUser = userIds => {
    // All other users
    if ([otherGroupMembers.length, 0].includes(userIds.length)) {
      handleChangeFilter({ createdByAccountIds: null, offset: 0 });
    } else {
      handleChangeFilter({ createdByAccountIds: userIds, offset: 0 });
    }
  };

  const handleScrollToBottom = async (currentFileList, offset) => {
    if (!isLoadMore || isFetchingRef.current) return;
    isFetchingRef.current = true;

    const records = await getInteractor(prefixKey).LocalFileGroupService.searchFileByCondition({
      ...filterCondition,
      offset: offset || displayItemTotal,
    });
    setIsLoadMore(records.length === PAGING_SIZE);

    const refactorFileList = await refactorFiles(prefixKey, records);

    const result = groupDataByDate(refactorFileList, currentFileList || fileList);
    setFileList(result);
    setDisplayItemTotal(displayItemTotal + records.length);

    isFetchingRef.current = false;
  };

  const handleJump2Message = async messageSourceId => {
    const message = (
      await getInteractor(prefixKey).LocalMessageService.getGroupMessage(groupId, {
        source_id: messageSourceId,
        limit: 1,
      })
    )[0];

    if (message && message.id) {
      const scrollData = { scrollToChildId: messageSourceId };
      if (message && message.thread_id) scrollData.threadingId = message.thread_id;
      dispatch(ConversationActions.conversationSet(scrollData));
    } else {
      showAlert({ content: getCommonLang("TXT_NOT_FOUND_MESSAGE"), alertProps: { severity: "error" } });
    }

    onClose();
    onCloseAll();
  };

  useEffect(async () => {
    setViewMode(AppConstant.VIEW_MODE.fetching);

    const getFileTimeout = setTimeout(() => {
      getInteractor(prefixKey)
        .LocalFileGroupService.searchFileByCondition({
          ...filterCondition,
          offset: 0,
          limit: PAGING_SIZE,
        })
        .then(listItems => {
          if (Array.isArray(listItems)) {
            setViewMode(listItems.length === 0 ? AppConstant.VIEW_MODE.empty : AppConstant.VIEW_MODE.list);
            setIsLoadMore(listItems.length === PAGING_SIZE);

            refactorFiles(prefixKey, listItems).then(result => {
              const listByDate = groupDataByDate(result);
              setFileList(listByDate);
              setDisplayItemTotal(listItems.length);

              if (result.length < PAGING_SIZE) handleScrollToBottom(listByDate, listItems.length);
            });
          } else {
            setViewMode(AppConstant.VIEW_MODE.empty);
            setIsLoadMore(false);
          }
        });
    }, AppConstant.DEBOUNCE_TIME);

    return () => clearTimeout(getFileTimeout);
  }, [prefixKey, filterCondition]);

  return (
    <AppDrawer
      open={open}
      onClose={onClose}
      title={getNSLang(LangConstant.NS_HOME_CONVERSATION, "TXT_GALLERY")}
      anchor="right"
      hideBackdrop={true}
      classes={{ wrapper: classes.drawerWrapper }}
    >
      <Stack height="100%" px={2} py={1} spacing={1}>
        <PrimaryTab tabArray={GALLERY_TAB_ARRAY} onChange={handleChangeTab} selectedTabIndex={selectedTab} />

        <SearchBar onChange={handleSearch} placeholder={getCommonLang("TXT_FINDING_FILE")} />

        <Stack direction="row" width="100%">
          <FileFilter onChangeFileType={handleFilterByType} arrayLabel={FILE_FILTER_OPTIONS} />
          <TimeFilter onChangeTime={handleFilterByTime} />
          {selectedTab === GALLERY_TABS.shareByOther.index && (
            <GroupMemberFilter groupMembers={otherGroupMembers} onChange={handleChangeUser} />
          )}
        </Stack>

        {viewMode === AppConstant.VIEW_MODE.empty && (
          <Stack margin="auto" pb={8}>
            <EmptyResult />
          </Stack>
        )}

        <InfiniteScroll className={classes.infiniteScroll} onScrollToBottom={handleScrollToBottom}>
          {viewMode === AppConstant.VIEW_MODE.list && (
            <Stack component={List} gap={2} disablePadding paddingBottom={2}>
              {Object.keys(fileList).map(date => (
                <ListFileByDate
                  key={date}
                  date={date}
                  list={fileList[date]}
                  className={classes.customFileList}
                  onJump2Message={handleJump2Message}
                  onClickMedia={clickMedia}
                />
              ))}
            </Stack>
          )}
          {viewMode === AppConstant.VIEW_MODE.fetching && <FileListLoading />}
        </InfiniteScroll>
      </Stack>
    </AppDrawer>
  );
};

export default Gallery;

const useStyles = makeStyles({
  drawerWrapper: { height: "100%" },

  infiniteScroll: {
    height: "calc(100% - 163px)",
    overflowY: "auto",
  },

  customFileList: {
    width: "100% !important",
  },
});
